import React from 'react';
import Text from '@amzn/meridian/text';
import DisplaySubMetric from './DisplaySubMetric';
import Row from '@amzn/meridian/row';
import { FormattedMessage } from 'react-intl';
import { DisplaySubMetricsProps } from '../types';
import {
  DARTRateDataCheck,
  SUBMETRIC_FONT_SIZE,
  SUBMETRIC_ROW_WIDTHS,
} from '../helper';
import { ControlledExpander } from './Util/ControlledExpander';
import Column from '@amzn/meridian/column';
import NoSubMetric from './Util/NoSubMetric';

const DisplayDartSubMetrics: React.FC<DisplaySubMetricsProps> = ({
  metrics,
  vertical,
}: DisplaySubMetricsProps): React.ReactElement => {
  if (!metrics.DART?.submetrics) {
    return <></>;
  }
  if (DARTRateDataCheck(metrics.DART?.submetrics!)) {
    return <NoSubMetric />;
  }

  const { dartUsedCount, totalHandledContact, escalatedContacts } =
    metrics.DART?.submetrics!;

  return (
    <Column spacing='none' width='100%'>
      {dartUsedCount && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_dartUsedCount'
                  defaultMessage='DART Used Count'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_dartUsedCount_def'
                defaultMessage='Sum of handled contacts during which the associate used DART to get assistance.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={dartUsedCount.datapoint} isBold />
          <DisplaySubMetric
            value={dartUsedCount.networkAverage}
            datapointNull={dartUsedCount.datapoint === null}
          />
        </Row>
      )}

      {escalatedContacts && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_escalatedContacts'
                  defaultMessage='Escalated Contacts'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_escalatedContacts_def'
                defaultMessage='Sum of handled contacts which resulted in an escalation.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={escalatedContacts.datapoint} isBold />
          <DisplaySubMetric
            value={escalatedContacts.networkAverage}
            datapointNull={escalatedContacts.datapoint === null}
          />
        </Row>
      )}

      {totalHandledContact && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_totalHandledContats'
                  defaultMessage='Total Handled Contacts'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_totalHandledContats_def'
                defaultMessage="Sum of contacts considered 'Handled'."
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={totalHandledContact.datapoint} isBold />
          <DisplaySubMetric
            value={totalHandledContact.networkAverage}
            datapointNull={totalHandledContact.datapoint === null}
          />
        </Row>
      )}
    </Column>
  );
};

export default DisplayDartSubMetrics;
